import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { getApiUrl } from '../config';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  Car,
  ShowerHead,
  Coffee,
  ExternalLink,
  ChevronRight,
  AlertCircle
} from 'lucide-react';

interface Venue {
  id: string;
  name: string;
  description: string | null;
  venueType: string;
  capacity: number;
  isIndoor: boolean;
  hasLighting: boolean;
  surfaceType: string;
  hourlyRate: number;
  peakHourRate: number | null;
  amenities: string[];
  photos: {
    id: string;
    url: string;
    caption: string | null;
    order: number;
  }[];
  availability: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isPeakHour: boolean;
  }[];
  avgRating: number | null;
  reviewCount: number;
}

interface Club {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  address: string;
  mapsUrl: string;
  city: string;
  province: string;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  email: string | null;
  hasParking: boolean;
  hasBathrooms: boolean;
  hasChangingRooms: boolean;
  hasBuffet: boolean;
  hasProShop: boolean;
  amenities: string[];
  badges: string[];
  photos: {
    id: string;
    url: string;
    caption: string | null;
    order: number;
  }[];
  venues: Venue[];
}

const PublicClubPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchClub();
    }
  }, [slug]);

  const fetchClub = async () => {
    try {
      const response = await fetch(getApiUrl(`/public/clubs/${slug}`));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Club no encontrado');
        }
        throw new Error('Error al cargar el club');
      }

      const data = await response.json();
      setClub(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    padding: '32px',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const headerContentStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '8px',
  };

  const locationStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.sizeBody,
    marginBottom: '16px',
  };

  const amenitiesRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '16px',
  };

  const amenityBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    color: theme.colors.primary,
    fontSize: theme.fonts.sizeSmall,
  };

  const contentStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '24px',
  };

  const venuesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  };

  const venueCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const venueImageStyle: React.CSSProperties = {
    width: '100%',
    height: '180px',
    backgroundColor: theme.colors.background,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const venueInfoStyle: React.CSSProperties = {
    padding: '20px',
  };

  const venueNameStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const venueTypeStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '12px',
  };

  const venuePriceStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const priceStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.primary,
  };

  const ratingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: theme.colors.warning,
  };

  const contactCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    padding: '24px',
    marginTop: '32px',
  };

  const contactRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    color: theme.colors.text,
  };

  const mapButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '16px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: theme.fonts.body,
    textDecoration: 'none',
  };

  const getDayName = (dayOfWeek: number): string => {
    const days = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
    return days[dayOfWeek];
  };

  if (loading) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{ color: theme.colors.textSecondary }}>Cargando...</div>
      </div>
    );
  }

  if (error || !club) {
    return (
      <div style={containerStyle}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 23, 68, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
          }}>
            <AlertCircle size={40} color={theme.colors.danger} />
          </div>
          <h1 style={{ ...titleStyle, fontSize: '1.5rem', marginBottom: '16px' }}>
            {error || 'Club no encontrado'}
          </h1>
          <p style={{ color: theme.colors.textSecondary, marginBottom: '24px' }}>
            El club que buscas no existe o no esta disponible.
          </p>
          <button
            style={{
              ...mapButtonStyle,
              width: 'auto',
              display: 'inline-flex',
            }}
            onClick={() => navigate('/venues')}
          >
            Buscar Canchas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={headerContentStyle}>
          <h1 style={titleStyle}>{club.name}</h1>
          <div style={locationStyle}>
            <MapPin size={18} />
            <span>{club.address}, {club.city}, {club.province}</span>
          </div>

          {club.description && (
            <p style={{
              color: theme.colors.textSecondary,
              lineHeight: '1.6',
              marginBottom: '16px',
              maxWidth: '800px',
            }}>
              {club.description}
            </p>
          )}

          <div style={amenitiesRowStyle}>
            {club.hasParking && (
              <span style={amenityBadgeStyle}>
                <Car size={16} /> Estacionamiento
              </span>
            )}
            {club.hasBathrooms && (
              <span style={amenityBadgeStyle}>
                <ShowerHead size={16} /> Banos
              </span>
            )}
            {club.hasChangingRooms && (
              <span style={amenityBadgeStyle}>
                <ShowerHead size={16} /> Vestuarios
              </span>
            )}
            {club.hasBuffet && (
              <span style={amenityBadgeStyle}>
                <Coffee size={16} /> Buffet
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Venues */}
        <h2 style={sectionTitleStyle}>Canchas Disponibles ({club.venues.length})</h2>

        {club.venues.length === 0 ? (
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.card,
            padding: '48px',
            textAlign: 'center',
            color: theme.colors.textSecondary,
          }}>
            <Building2 size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>Este club aun no tiene canchas disponibles</p>
          </div>
        ) : (
          <div style={venuesGridStyle}>
            {club.venues.map((venue) => (
              <div
                key={venue.id}
                style={venueCardStyle}
                onClick={() => navigate(`/venues/${venue.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={venueImageStyle}>
                  {venue.photos.length > 0 ? (
                    <img
                      src={venue.photos[0].url}
                      alt={venue.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Building2 size={48} color={theme.colors.textSecondary} style={{ opacity: 0.3 }} />
                  )}
                </div>
                <div style={venueInfoStyle}>
                  <h3 style={venueNameStyle}>{venue.name}</h3>
                  <div style={venueTypeStyle}>
                    {venue.venueType} - {venue.capacity} jugadores - {venue.surfaceType}
                  </div>

                  {venue.availability.length > 0 && (
                    <div style={{
                      fontSize: theme.fonts.sizeSmall,
                      color: theme.colors.textSecondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <Clock size={14} />
                      <span>
                        {venue.availability[0].startTime} - {venue.availability[0].endTime}
                      </span>
                    </div>
                  )}

                  <div style={venuePriceStyle}>
                    <span style={priceStyle}>${venue.hourlyRate}/hora</span>
                    {venue.avgRating && (
                      <span style={ratingStyle}>
                        <Star size={16} fill={theme.colors.warning} />
                        {venue.avgRating.toFixed(1)} ({venue.reviewCount})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Card */}
        <div style={contactCardStyle}>
          <h3 style={{ ...sectionTitleStyle, fontSize: '1.2rem', marginBottom: '16px' }}>
            Contacto
          </h3>

          {club.phone && (
            <div style={contactRowStyle}>
              <Phone size={20} color={theme.colors.primary} />
              <a href={`tel:${club.phone}`} style={{ color: theme.colors.text, textDecoration: 'none' }}>
                {club.phone}
              </a>
            </div>
          )}

          {club.email && (
            <div style={contactRowStyle}>
              <Mail size={20} color={theme.colors.primary} />
              <a href={`mailto:${club.email}`} style={{ color: theme.colors.text, textDecoration: 'none' }}>
                {club.email}
              </a>
            </div>
          )}

          <a
            href={club.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={mapButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00D96F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary;
            }}
          >
            <MapPin size={20} />
            Ver en Google Maps
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicClubPage;
